import { useEffect, useMemo, useState } from "react";
import {
  Page,
  StickyStackItem,
  ClinicFilterBar,
  usePersistedState,
  Loading,
  SegmentedControl,
} from "@common";
import { torontoToday } from "@utils";
import { useBankAccounts, useEntities } from "@api/hooks";
import { getDayWindow, shiftISO } from "./ClinicCashFlowCard";
import EntityCashFlowTable from "./EntityCashFlowTable";
import DailyBankBalance from "./DailyBankBalance";
import SchedulePayment from "./SchedulePayment";
// Hidden for now — see TAB_OPTIONS below.
// import Actions from "./actions/Actions";

// All Dens N Dente Cash Flow tabs, in display order. Every role the route admits
// sees all of them.
//
// Actions stays COMMENTED OUT rather than deleted: it renders a clinic-wide view
// and ignores the entity selection, which reads as a bug on this page. It cannot
// be made entity-aware without backend work — a transfer needs a
// `netsuite_account_id` on both sides, and no entity account has one.
//
// Schedule Payment was hidden for the same reason and is now BACK: it keys on
// `bank_accounts.BankAccount` instead of a clinic FK, and takes an entity-scoped
// mode, so it offers only this page's entity-level accounts.

function Chevron({ open }) {
  return (
    <svg
      className={`h-3 w-3 shrink-0 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Mirrors `AccountType.choices` on backend/apps/bank_accounts/models.py. Used
// only for the account picker's subtitle; an unmapped type falls back to its raw
// value rather than rendering blank.
const ACCOUNT_TYPE_LABELS = {
  bank: "Bank",
  credit_card: "Credit Card",
  line_of_credit: "Line of Credit",
};

const TAB_OPTIONS = [
  { key: "cashFlow", label: "Cash Flow" },
  { key: "schedulePayment", label: "Schedule Payment" },
  { key: "dailyBankBalance", label: "Daily Balance" },
  // { key: "actions", label: "Actions" },
];

/**
 * Entity-scoped Cash Flow page — the sibling of AccessClinicCashFlow.
 *
 * AccessClinicCashFlow groups by CLINIC. This page groups by ENTITY: the filter
 * bar has no Area Manager (an entity has none) and no clinic picker; instead a
 * MULTI-SELECT Entity picker — the same component as the clinic picker, just
 * relabelled — drives everything below it. Empty selection means all entities,
 * matching how the clinic filter behaves. Accounts come from
 * `bank_accounts.BankAccount` by `entity_id` **and `has_clinic=false`**, including
 * the credit cards and lines of credit that no clinic owns. The clinic filter
 * matters: a few accounts carry both a clinic and an entity (the SFC
 * pseudo-clinic's "Bank Balance : RBC S.F. Corporation - [9411]"), and those are
 * the clinic-scoped pages' business, not this one's.
 *
 * Selection is keyed on entity PK, not name: names contain apostrophes
 * ("Dens 'N Dente Healthcare - WS") and matching on them is needlessly fragile.
 *
 * Tab status:
 *   - Cash Flow        → entity-scoped: one row per bank account, one column per
 *                        day (`EntityCashFlowTable`), grouped under an entity
 *                        header when more than one entity is selected.
 *   - Daily Bank Balance → entity-scoped via `DailyBankBalance entityIds=[...]`,
 *                        so every one of the entity's entity-level accounts can be
 *                        updated daily. The clinic-scoped version of that page
 *                        deliberately excludes these accounts, and this one
 *                        excludes its clinic-owned ones — exact complements.
 *   - Schedule Payment → entity-scoped via `SchedulePayment entityIds=[...]`.
 *                        Offers ONLY entity-level accounts (`has_clinic=false`),
 *                        the exact complement of the clinic-scoped version on
 *                        AccessClinicCashFlow, so neither page can schedule
 *                        against the other's accounts.
 *   - Actions          → HIDDEN (commented out in TAB_OPTIONS and in the render
 *                        branch). It renders clinic-wide data and ignores the
 *                        entity selection. Unblocking it needs backend work: a
 *                        transfer requires a `netsuite_account_id` on both sides
 *                        and no entity account has one.
 *
 * Every role the route admits sees every visible tab — Payroll included, which
 * used to be narrowed to Daily Bank Balance because the other tabs 403'd on read
 * (see AccessClinicCashFlow for the backend fix). Write access is derived per tab
 * from the capability helpers in utils/roles.js — not decided here.
 */
export default function DensNDenteCashFlow() {
  const [activeTab, setActiveTab] = useState(TAB_OPTIONS[0].key);

  // ── entity selection ───────────────────────────────────────────────────────
  const entitiesQuery = useEntities({});
  const entities = useMemo(
    () => entitiesQuery.data?.results ?? entitiesQuery.data ?? [],
    [entitiesQuery.data],
  );
  // Multi-select, same as the clinic picker. Keyed on entity PK rather than name:
  // names contain apostrophes ("Dens 'N Dente Healthcare - WS") and matching on
  // them is needlessly fragile. `name` feeds the picker's search box.
  const entityOptions = useMemo(
    () => entities.map((e) => ({ value: e.id, label: e.name, name: e.name })),
    [entities],
  );

  const [selectedEntityIds, setSelectedEntityIds] = usePersistedState("densNDente:entityIds", []);

  // Drop ids that no longer exist (entity deleted) so a stale selection can't
  // silently filter everything out.
  useEffect(() => {
    if (entityOptions.length === 0 || selectedEntityIds.length === 0) return;
    const valid = new Set(entityOptions.map((o) => o.value));
    const pruned = selectedEntityIds.filter((id) => valid.has(id));
    if (pruned.length !== selectedEntityIds.length) setSelectedEntityIds(pruned);
  }, [entityOptions, selectedEntityIds, setSelectedEntityIds]);

  // Empty selection = NOTHING, matching how the clinic filter behaves. Every
  // query below fans out per entity, so resolving an empty picker to "all of
  // them" made opening the page the most expensive thing it does. Nothing is
  // fetched until an entity is chosen.
  const effectiveEntityIds = selectedEntityIds;

  // ── account selection ──────────────────────────────────────────────────────
  // Fetched here rather than only inside the tabs because the picker has to list
  // them. The filter shape is IDENTICAL to the one EntityCashFlowTable and
  // DailyBankBalance use, so react-query serves all three from one cache entry
  // (the query key includes the filters object) instead of refetching.
  //
  // `has_clinic=false` keeps this page to ENTITY-LEVEL accounts only. Without it,
  // an account that carries BOTH a clinic and an entity leaks in — the SFC
  // pseudo-clinic's "Bank Balance : RBC S.F. Corporation - [9411]" is exactly
  // that, and it belongs on the clinic-scoped pages, not here.
  const accountsQuery = useBankAccounts(
    {
      status: "active",
      entity_id: effectiveEntityIds.join(","),
      has_clinic: "false",
      limit: 500,
    },
    { enabled: effectiveEntityIds.length > 0 },
  );
  const allAccounts = useMemo(
    () => accountsQuery.data?.results ?? accountsQuery.data ?? [],
    [accountsQuery.data],
  );

  // Every account of the selected entities — bank, credit card and line of
  // credit alike. `name` feeds the picker's search box; the subtitle carries the
  // currency and type so two similarly-named cards stay tellable apart.
  const accountOptions = useMemo(
    () =>
      allAccounts.map((a) => ({
        value: a.id,
        label: a.label,
        name: a.label,
        subtitle: [a.currency, ACCOUNT_TYPE_LABELS[a.account_type] ?? a.account_type]
          .filter(Boolean)
          .join(" · "),
      })),
    [allAccounts],
  );

  const [selectedAccountIds, setSelectedAccountIds] = usePersistedState(
    "densNDente:accountIds",
    [],
  );

  // Drop ids that are no longer selectable — the account was closed, or the
  // entity filter changed underneath it — so a stale selection can't silently
  // empty the table. Same guard as the entity one above.
  useEffect(() => {
    if (accountOptions.length === 0 || selectedAccountIds.length === 0) return;
    const valid = new Set(accountOptions.map((o) => o.value));
    const pruned = selectedAccountIds.filter((id) => valid.has(id));
    if (pruned.length !== selectedAccountIds.length) setSelectedAccountIds(pruned);
  }, [accountOptions, selectedAccountIds, setSelectedAccountIds]);

  // Empty selection = every account OF THE SELECTED ENTITIES. Unlike the entity
  // picker this stays "all", because it only ever narrows a set the entity
  // picker has already bounded — there is no unbounded fetch to avoid here.
  const effectiveAccountIds = useMemo(
    () => (selectedAccountIds.length > 0 ? selectedAccountIds : accountOptions.map((o) => o.value)),
    [selectedAccountIds, accountOptions],
  );

  // ── window for the Cash Flow table ─────────────────────────────────────────
  // Same controls as the clinic cash flow: a rolling 30/60-day window from
  // `rangeStart` (defaults to today) and daily/weekly columns, each persisted
  // under its own key so the two pages don't share a setting.
  const [rangeStart, setRangeStart] = usePersistedState("densNDente:rangeStart", () =>
    torontoToday(),
  );
  const [spanDays, setSpanDays] = usePersistedState("densNDente:spanDays", 30);
  const [viewMode, setViewMode] = usePersistedState("densNDente:viewMode", "daily");
  // "account" → one block per account; "card" → every credit card combined into
  // one, mirroring By Clinic / By Company on AccessClinicCashFlow.
  const [groupMode, setGroupMode] = usePersistedState("densNDente:groupMode", "account");
  const days = useMemo(() => getDayWindow(rangeStart, spanDays), [rangeStart, spanDays]);
  const rangeEnd = days.length ? days[days.length - 1].date : undefined;

  // Which account blocks are collapsed in the grid. Owned here rather than in
  // EntityCashFlowTable so the Expand/Collapse All button can sit in the toolbar
  // above it and still drive the same set the per-account chevrons do. Holds the
  // exceptions only, so a newly-added account appears expanded.
  const [collapsedAccounts, setCollapsedAccounts] = useState(() => new Set());
  const allAccountsExpanded = collapsedAccounts.size === 0;

  function toggleAllAccounts() {
    // Collapsing targets the accounts actually on screen — collapsing ids that
    // the Accounts filter has hidden would leave them shut when it's cleared.
    setCollapsedAccounts(allAccountsExpanded ? new Set(effectiveAccountIds) : new Set());
  }

  const hasEntities = entityOptions.length > 0;
  // "None configured" and "none picked yet" are different states and read very
  // differently to someone looking at a blank tab. Only the Cash Flow tab uses
  // this for the second case: the other two render their own filter bar with
  // the entity picker in it, so replacing them with a notice would leave no
  // picker to make a selection with — they show their own empty state instead.
  const cashFlowNotice = !hasEntities
    ? "No entities configured."
    : effectiveEntityIds.length === 0
      ? "Select one or more entities to view cash flow."
      : null;

  // id → name, so the table can group and label rows by entity without refetching.
  const entityNameById = useMemo(
    () => Object.fromEntries(entities.map((e) => [e.id, e.name])),
    [entities],
  );

  return (
    <Page>
      {/* Tab bar */}
      <StickyStackItem className="-mx-6 px-6">
        <div className="mb-4 flex gap-1 border-b border-palette-slate-divider">
          {TAB_OPTIONS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? "border-palette-indigo-label text-palette-indigo-badge"
                  : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </StickyStackItem>

      {activeTab === "cashFlow" ? (
        <>
          {/* Date range + Entity + Accounts — no Area Manager, no clinic picker. */}
          <ClinicFilterBar
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            rangeDays={spanDays}
            rangeIsDefault={rangeStart === torontoToday()}
            onRangeShift={(dir) => setRangeStart((s) => shiftISO(s, dir * spanDays))}
            onRangeReset={() => setRangeStart(torontoToday())}
            // Either end of the window is pickable; the filter bar converts an
            // end-date pick into its start, since the span fixes the other end.
            onRangeStartChange={setRangeStart}
            showAreaManager={false}
            entityOptions={entityOptions}
            entityValue={selectedEntityIds}
            onEntityChange={setSelectedEntityIds}
            accountOptions={accountOptions}
            accountValue={selectedAccountIds}
            onAccountChange={setSelectedAccountIds}
          />

          {/* By Account / By Visa on the left, span + view mode + expand on the
              right — the same split as the clinic cash flow's toolbar. */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <SegmentedControl
              options={[
                { value: "account", label: "By Account" },
                { value: "card", label: "By Visa" },
              ]}
              value={groupMode}
              onChange={setGroupMode}
            />

            <div className="flex flex-wrap items-center gap-2">
              <SegmentedControl
                options={[
                  { value: 30, label: "30 Days" },
                  { value: 60, label: "60 Days" },
                ]}
                value={spanDays}
                onChange={setSpanDays}
              />
              <div className="h-6 w-px bg-palette-gray-divider" />
              <SegmentedControl
                options={[
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                ]}
                value={viewMode}
                onChange={setViewMode}
              />
              {/* Hidden in card mode: there is one combined row, and this button
                drives the per-ACCOUNT collapse set, which that row is not in. Its
                own chevron still collapses it. */}
              {groupMode === "account" && (
                <button
                  type="button"
                  onClick={toggleAllAccounts}
                  className="flex items-center gap-1.5 rounded-lg border border-palette-gray-divider bg-white px-4 py-2 text-sm font-medium text-palette-gray-heading shadow-sm hover:bg-palette-gray-subtle transition-colors"
                >
                  <Chevron open={allAccountsExpanded} />
                  {allAccountsExpanded ? "Collapse All" : "Expand All"}
                </button>
              )}
            </div>
          </div>

          {entitiesQuery.isLoading ? (
            <Loading text="Loading entities..." />
          ) : cashFlowNotice ? (
            <div className="flex items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-sm text-palette-gray-placeholder">
              {cashFlowNotice}
            </div>
          ) : (
            <EntityCashFlowTable
              entityIds={effectiveEntityIds}
              accountIds={effectiveAccountIds}
              entityNameById={entityNameById}
              days={days}
              viewMode={viewMode}
              collapsedAccounts={collapsedAccounts}
              onCollapsedAccountsChange={setCollapsedAccounts}
              groupMode={groupMode}
            />
          )}
        </>
      ) : activeTab === "schedulePayment" ? (
        hasEntities ? (
          // Entity-scoped: only this page's entity-level accounts are offered,
          // and payments group by entity. The account picker above is NOT passed
          // through — Schedule Payment scopes by its own account list, which is
          // already narrower (entity-level only) than that picker's.
          <SchedulePayment
            entityIds={effectiveEntityIds}
            entityOptions={entityOptions}
            entityValue={selectedEntityIds}
            onEntityChange={setSelectedEntityIds}
          />
        ) : (
          <div className="flex items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-sm text-palette-gray-placeholder">
            {entitiesQuery.isLoading ? "Loading entities..." : "No entities configured."}
          </div>
        )
      ) : // Daily Bank Balance. The entity picker is passed INTO the tab so its own
      // filter bar renders Date + Entity on one row, rather than stacking a
      // second bar here.
      //
      // The Actions branch is commented out alongside its TAB_OPTIONS entry —
      // restore both together:
      //   ) : activeTab === "actions" ? (
      //     <Actions />
      hasEntities ? (
        <DailyBankBalance
          entityIds={effectiveEntityIds}
          accountIds={effectiveAccountIds}
          entityOptions={entityOptions}
          entityValue={selectedEntityIds}
          onEntityChange={setSelectedEntityIds}
          accountOptions={accountOptions}
          accountValue={selectedAccountIds}
          onAccountChange={setSelectedAccountIds}
        />
      ) : (
        <div className="flex items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-sm text-palette-gray-placeholder">
          {entitiesQuery.isLoading ? "Loading entities..." : "No entities configured."}
        </div>
      )}
    </Page>
  );
}
